import api from '../apiClient';

// ========================================
// ROUTES CRUD DE BASE
// ========================================

// Récupérer toutes les entreprises
export const getCompanies = () =>
  api.get('/companies');

// Récupérer une entreprise par ID
export const getCompanyById = (id) =>
  api.get(`/companies/${id}`);

// Créer une nouvelle entreprise
export const createCompany = (data) =>
  api.post('/companies', data);

// Mettre à jour une entreprise
export const updateCompany = (id, data) =>
  api.put(`/companies/${id}`, data);

// Supprimer une entreprise
export const deleteCompany = (id) =>
  api.delete(`/companies/${id}`);

// ========================================
// ROUTES SPÉCIALISÉES
// ========================================

// Récupérer l'entreprise de l'utilisateur connecté
export const getMyCompany = () =>
  api.get('/companies/my-company');

// Statut de la checklist « Bien démarrer » (product tour)
export const getGettingStarted = () =>
  api.get('/companies/getting-started');

// Récupérer l'entreprise d'un utilisateur
export const getCompanyByUserId = (userId) =>
  api.get(`/companies/user/${userId}`);

// Rechercher des entreprises
export const searchCompanies = (query) =>
  api.get(`/companies/search/${query}`);

// Récupérer les entreprises par type
export const getCompaniesByType = (type) =>
  api.get(`/companies/type/${type}`);

// Upload d'images
export const uploadCompanyPhoto = async (companyId, file) => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await api.post(`/companies/${companyId}/upload/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000, // 30 second timeout
  });
  return response.data.url;
}

// ========================================
// URGENT NOTIFICATIONS (GROUPS)
// ========================================

// Remplacer la liste des groupes de notifications urgentes
export const putUrgentNotificationGroups = (companyId, groups) =>
  api.put(`/companies/${companyId}/urgent-notifications`, { groups });

// Ajouter un groupe aux notifications urgentes
export const addUrgentNotificationGroup = (companyId, groupId) =>
  api.post(`/companies/${companyId}/urgent-notifications/groups`, { groupId });

// Retirer un groupe des notifications urgentes
export const removeUrgentNotificationGroup = (companyId, groupId) =>
  api.delete(`/companies/${companyId}/urgent-notifications/groups/${groupId}`);

// ========================================
// ROUTES POUR LA CONFIGURATION STRIPE
// ========================================

// Mettre à jour la configuration Stripe
export const updateStripeConfig = (companyId, stripeData) =>
  api.put(`/companies/${companyId}/stripe`, stripeData);

// Vérifier le statut Stripe
export const getStripeStatus = (companyId) =>
  api.get(`/companies/${companyId}/stripe-status`);

// ========================================
// FONCTIONS UTILITAIRES
// ========================================

// Créer une entreprise avec validation
export const createCompanyWithValidation = async (companyData) => {
  try {
    // Validation des champs requis
    const requiredFields = ['name', 'address', 'ownerContact'];
    const missingFields = requiredFields.filter(field => !companyData[field]);

    if (missingFields.length > 0) {
      throw new Error(`Champs manquants: ${missingFields.join(', ')}`);
    }

    // Validation du contact propriétaire
    if (!companyData.ownerContact.name || !companyData.ownerContact.email) {
      throw new Error('Informations de contact propriétaire incomplètes');
    }

    const response = await createCompany(companyData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

// Mettre à jour une entreprise avec validation
export const updateCompanyWithValidation = async (id, updateData) => {
  try {
    const response = await updateCompany(id, updateData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

// Supprimer une entreprise avec confirmation
export const deleteCompanyWithConfirmation = async (id) => {
  try {
    await deleteCompany(id);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

// Mettre à jour la configuration Stripe avec validation
export const updateStripeWithValidation = async (companyId, stripeData) => {
  try {
    if (!companyId) {
      throw new Error('ID de l\'entreprise requis');
    }

    const response = await updateStripeConfig(companyId, stripeData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

// Mettre à jour les horaires de check-in/check-out
export const updateCheckoutTimes = async (companyId, { defaultCheckInTime, defaultCheckOutTime }) => {
  return await api.put(`/companies/${companyId}/checkout-times`, {
    defaultCheckInTime,
    defaultCheckOutTime,
  });
};

// Forcer la mise à jour des horaires sur toutes les unités
export const forceUpdateAllUnitsCheckoutTimes = async (companyId, { defaultCheckInTime, defaultCheckOutTime }) => {
  return await api.put(`/companies/${companyId}/force-checkout-times`, {
    defaultCheckInTime,
    defaultCheckOutTime,
  });
};

// Compter le nombre d'unités d'une company
export const getUnitsCount = async (companyId) => {
  return await api.get(`/companies/${companyId}/units-count`);
};

// ========================================
// FINANCIAL SETTINGS (Owner Portal Visibility)
// ========================================

// Get a reservation by confirmation code for financial config UI
// Returns reservation data with validation of platform match
export const getReservationByCode = async (companyId, confirmationCode, expectedPlatform = null) => {
  const params = new URLSearchParams();
  params.append('confirmationCode', confirmationCode);
  if (expectedPlatform) params.append('expectedPlatform', expectedPlatform);
  return await api.get(`/companies/${companyId}/reservation-by-code?${params.toString()}`);
};

// Get a sample reservation with all fees for financial config UI (legacy - auto fetch)
export const getSampleReservation = async (companyId, platform = null) => {
  const params = platform ? `?platform=${platform}` : '';
  return await api.get(`/companies/${companyId}/sample-reservation${params}`);
};

// Get list of available platforms with reservations
export const getAvailablePlatforms = async (companyId) => {
  return await api.get(`/companies/${companyId}/available-platforms`);
};

// Get financial visibility settings
export const getFinancialSettings = async (companyId) => {
  return await api.get(`/companies/${companyId}/financial-settings`);
};

// Update financial visibility settings
export const updateFinancialSettings = async (companyId, settings) => {
  return await api.put(`/companies/${companyId}/financial-settings`, settings);
};

// Dry-run preview of the monthly owner-statements recap (no DB write, no email).
// Returns aggregated owners + rendered email + the recap file as base64.
export const testAutopilot = async (companyId, { month, year } = {}) => {
  return await api.post(`/companies/${companyId}/test-autopilot`, { month, year });
};

// What the guest receives on each Booking Booster channel (built by the real senders)
export const fetchBookingBoosterPreviews = (lang = 'fr', discount) =>
  api.get('/companies/booking-booster/previews', { params: { lang, discount } });
